<template>
  <VueFinalModal
    v-model="isOpen"
    content-class="vfm-dialog-500"
  >
    <div class="modal-content modal-grade-content">
      <button
        type="button"
        class="modal-close"
        @click="$modal.hide('gradeEditModal')"
      >
        <em class="icon-close"></em>
      </button>
      <div class="modal-body mx-auto">
        <div class="modal-title">
          <h1 class="mb-4">Edit Grade</h1>
        </div>
        <div>
          <div class="student-info mb-3">
            <p><strong>Student:</strong> {{ learnerName }}</p>
            <p><strong>Skill:</strong> {{ skillName }}</p>
            <p><strong>Current Grade:</strong> {{ currentValue }}</p>
          </div>
          
          <VeeForm ref="observer" v-slot="{ handleSubmit }" as="div">
            <form @submit.prevent="handleSubmit(save)">
              <div class="form-group mb-3">
                <label for="gradeValue" class="form-label">New Grade (0-{{ maxPoints }}):</label>
                <VeeField
                  v-slot="{ field, errors }"
                  :rules="`required|min_value:0|max_value:${maxPoints}|numeric`"
                  name="grade"
                  v-model.number="gradeValue"
                >
                  <input
                    id="gradeValue"
                    v-bind="field"
                    type="number"
                    min="0"
                    :max="maxPoints"
                    step="0.1"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                  />
                  <div class="invalid-feedback">{{ errors[0] }}</div>
                </VeeField>
              </div>
              
              <div class="form-group mb-4">
                <label for="reason" class="form-label">
                  Reason for change: <span class="required">*</span>
                </label>
                <VeeField
                  v-slot="{ field, errors }"
                  rules="required|min:3"
                  name="reason"
                  v-model="reason"
                >
                  <textarea
                    id="reason"
                    v-bind="field"
                    rows="3"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length }"
                    placeholder="e.g., Late submission accommodation, Grading error correction, Extra credit work"
                  />
                  <div class="invalid-feedback">{{ errors[0] }}</div>
                </VeeField>
              </div>
              
              <div class="d-flex flex-row justify-content-between">
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="isLoading"
                >
                  <span v-if="isLoading">Saving...</span>
                  <span v-else>Save Changes</span>
                </button>
                <button
                  type="button"
                  class="btn"
                  @click="$modal.hide('gradeEditModal')"
                  :disabled="isLoading"
                >
                  Cancel
                </button>
              </div>
            </form>
          </VeeForm>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script>
import { getModalState } from '@/plugins/modal-compat';
export default {
  name: "GradeEditModal",
  computed: {
    isOpen: {
      get() { return getModalState('gradeEditModal').isOpen.value; },
      set(val) { getModalState('gradeEditModal').isOpen.value = val; }
    },
    modalParams() { return getModalState('gradeEditModal').params.value; }
  },
  watch: {
    // Fires when the modal transitions from closed → open, exactly like @before-open
    isOpen(val) {
      if (val && this.modalParams) {
        this.beforeOpen({ params: this.modalParams });
      }
    }
  },
  data() {
    return {
      learnerName: "",
      skillName: "",
      currentValue: 0,
      gradeValue: 0,
      maxPoints: 100,
      reason: "",
      onSave: null,
      isLoading: false,
      userSectionId: null,
      uloAssmntSkillId: null
    }
  },
  methods: {
    beforeOpen(event) {
      const params = event.params;
      this.learnerName = params.learnerName || "";
      this.skillName = params.skillName || "";
      this.currentValue = params.currentValue || 0;
      this.gradeValue = params.currentValue || 0;
      this.maxPoints = params.maxPoints || 100;
      this.reason = "";
      this.onSave = params.onSave;
      this.userSectionId = params.userSectionId;
      this.uloAssmntSkillId = params.uloAssmntSkillId;
      this.isLoading = false;
      
      // Reset validation state
      if (this.$refs.observer) {
        this.$refs.observer.resetForm();
      }
    },
    async save() {
      if (!this.onSave) {
        console.error("No save callback provided");
        return;
      }
      
      this.isLoading = true;
      
      try {
        await this.onSave({
          newValue: this.gradeValue,
          reason: this.reason,
          userSectionId: this.userSectionId,
          uloAssmntSkillId: this.uloAssmntSkillId
        });
        
        this.$modal.hide('gradeEditModal');
      } catch (error) {
        console.error("Failed to save grade:", error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-grade-content {
  // Override global 800px/100vh panel sizing — this modal is a small centered dialog
  width: auto !important;
  height: auto !important;
  margin: 0 !important;
  padding: 20px;
  
  .modal-title h1 {
    color: #333;
    font-size: 24px;
    margin-bottom: 20px;
  }
  
  .student-info {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    border-left: 4px solid #3dbc9e;
    
    p {
      margin-bottom: 8px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  
  .form-label {
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }
  
  .required {
    color: #dc3545;
  }
  
  .form-control {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 10px 12px;
    font-size: 14px;
    
    &:focus {
      border-color: #3dbc9e;
      box-shadow: 0 0 0 0.2rem rgba(61, 188, 158, 0.25);
    }
    
    &.is-invalid {
      border-color: #dc3545;
    }
  }
  
  .invalid-feedback {
    display: block;
    font-size: 0.875rem;
    color: #dc3545;
    margin-top: 5px;
  }
  
  .btn {
    padding: 10px 20px;
    border-radius: 4px;
    font-weight: 500;
    
    &.btn-primary {
      background-color: #3dbc9e;
      border-color: #3dbc9e;
      color: white;
      
      &:hover:not(:disabled) {
        background-color: #339d7f;
        border-color: #339d7f;
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
    
    &:not(.btn-primary) {
      background-color: #f8f9fa;
      border: 1px solid #dee2e6;
      color: #495057;
      
      &:hover:not(:disabled) {
        background-color: #e9ecef;
      }
    }
  }
}

.modal-close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #6c757d;
  
  &:hover {
    color: #495057;
  }
}
</style>